import { Feature, isFeatureEnabled } from '@dadohr/shared/domainTypes/features';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { SubscriptionProvider } from '@dadohr/shared/domainTypes/subscription';
import styled from '@emotion/styled';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography
} from '@material-ui/core';
import { capitalize } from 'lodash';
import React, { useMemo, useState } from 'react';
import { InfoBox } from '../../../../components/InfoBox';
import { LinkExternal } from '../../../../components/LinkExternal';
import { NoAccess } from '../../../../components/NoAccess';
import ENV from '../../../../env.json';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import { PageToolbar, PageToolbarTitle } from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useBooleanQueryParam, useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { useFeatureEnabled } from '../../../../services/features';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import { toMoment } from '../../../../services/time';
import { useOrgSideNav } from '../sideNav';
import {
  StripeCheckoutButton,
  StripeCustomerPortalButton,
  StripeProvider
} from './Stripe';

const PLAN_DEFINITIONS: {
  key: string;
  provider: SubscriptionProvider.STRIPE;
  label: (t: UnsafeTFunction) => string;
  lines: (t: UnsafeTFunction) => string[];
  billingFrequency: (t: UnsafeTFunction) => string;
  priceId: string;
  featureFlag: Feature;
  currency: string;
}[] = [
  {
    key: 'STRIPE_STARTER_PLAN_EUR',
    provider: SubscriptionProvider.STRIPE,
    label: (t) => t('Starter Plan'),
    lines: (t) => [
      t('Pay €125 per experience participant'),
      t('Billed quarterly')
    ],
    billingFrequency: (t) => t('quarterly'),
    priceId: ENV.stripe.priceIds.PLAN_STARTER_EUR,
    featureFlag: 'PLAN_STARTER_EUR',
    currency: 'EUR'
  },
  {
    key: 'STRIPE_STARTER_PLAN_USD_1M',
    provider: SubscriptionProvider.STRIPE,
    label: (t) => t('Starter Plan'),
    lines: (t) => [
      t('Pay $125 per experience participant'),
      t('Billed monthly')
    ],
    billingFrequency: (t) => t('monthly'),
    priceId: ENV.stripe.priceIds.PLAN_STARTER_USD_1M,
    featureFlag: 'PLAN_STARTER_USD_1M',
    currency: 'USD' // this plan is actually dynamic and supports both EUR and USD - we'll adapt this later
  }
];

const URLS = {
  TermsOfService: 'https://dadohr.com/legal/terms-and-conditions',
  PrivacyPolicy: 'https://dadohr.com/legal/privacy-policy'
};

const Container = styled('div')<{ active?: boolean }>((p) => ({
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  padding: p.theme.spacing(2),
  borderColor: p.active
    ? p.theme.custom.colors.green.w500
    : p.theme.custom.border.color,

  display: 'grid',
  gap: p.theme.spacing(2),
  gridTemplateColumns: '3fr 1fr',
  alignItems: 'center',
  maxWidth: '66%',
  marginBottom: p.theme.spacing(4)
}));

const SubscriptionDetailsGrid = styled('div')`
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-column-gap: ${(p) => p.theme.spacing(2)}px;
  grid-row-gap: ${(p) => p.theme.spacing(1)}px;
`;

const SubscriptionDetailsItem = ({
  label,
  value
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}) => {
  return (
    <>
      <Typography component="label" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </>
  );
};

const ActiveSubscription = ({ org }: { org: IOrganization }) => {
  const { t } = useUnsafeTranslation();
  const { subscription, tz } = org;
  if (subscription?.provider !== SubscriptionProvider.STRIPE) {
    return null;
  }
  if (!subscription.subscribedUntil) {
    return null;
  }
  const planDefinition = PLAN_DEFINITIONS.find(
    (p) => p.priceId === subscription.plan
  );
  if (!planDefinition) {
    return null;
  }
  const label = planDefinition.label(t);
  const frequency = planDefinition.billingFrequency(t);
  return (
    <Container>
      <FlexContainer
        spacing={2}
        direction="column"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <Typography variant="h5">{label}</Typography>
        <SubscriptionDetailsGrid>
          <SubscriptionDetailsItem
            label="Next Invoice"
            value={toMoment(subscription.subscribedUntil)
              .tz(tz)
              .format(org.dateFormats.MMDDYYYY)}
          />
          <SubscriptionDetailsItem
            label="Billing Frequency"
            value={capitalize(frequency)}
          />
        </SubscriptionDetailsGrid>
      </FlexContainer>
      <StripeCustomerPortalButton
        orgId={org.id}
        subscription={subscription}
        variant="outlined"
      />
    </Container>
  );
};

const ContentOrgSubscription = () => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const { features } = org;
  const sideNav = useOrgSideNav();

  const hideTosAcceptance = useFeatureEnabled('HIDE_TOS_ACCEPTANCE');
  const [checkoutSuccess] = useBooleanQueryParam('checkoutSuccess', false);

  const accepted = !!org.termsOfServiceAndPrivacyPolicy?.accepted;
  const [state, setState] = useState({
    tos: hideTosAcceptance || accepted,
    pp: hideTosAcceptance || accepted
  });

  const selectablePlans = useMemo(
    () =>
      PLAN_DEFINITIONS.filter((p) => isFeatureEnabled(features, p.featureFlag)),
    [features]
  );

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('pageTitle.orgSubscription')}
          </Typography>
        </PageToolbarTitle>
      </PageToolbar>

      {checkoutSuccess && (
        <Section spacing={4}>
          <InfoBox color="green">
            {t('Thanks for choosing Dado! Your subscription is now active.')}
          </InfoBox>
        </Section>
      )}

      {org.subscription && (
        <Section>
          <ActiveSubscription org={org} />
        </Section>
      )}

      {!org.subscription && (
        <Section>
          {selectablePlans.map((plan) => {
            const label = plan.label(t);
            const lines = plan.lines(t);
            return (
              <Container key={plan.key}>
                <FlexContainer
                  spacing={2}
                  direction="column"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                >
                  <Typography variant="h5">{label}</Typography>

                  <Typography variant="body1">
                    {lines.map((l, i) => (
                      <div key={i}>{`• ${l}`}</div>
                    ))}
                  </Typography>

                  {!hideTosAcceptance && (
                    <FormGroup>
                      <FormControlLabel
                        control={<Checkbox color="primary" />}
                        checked={state.tos}
                        label={
                          <div>
                            I have read and accept the{' '}
                            <LinkExternal
                              color="primary"
                              href={URLS.TermsOfService}
                            >
                              Terms of Service
                            </LinkExternal>
                          </div>
                        }
                        onChange={(ev, checked) =>
                          setState((x) => ({ ...x, tos: checked }))
                        }
                      />

                      <FormControlLabel
                        control={<Checkbox color="primary" />}
                        checked={state.pp}
                        label={
                          <div>
                            I have read and accept the{' '}
                            <LinkExternal
                              color="primary"
                              href={URLS.PrivacyPolicy}
                            >
                              Privacy Policy
                            </LinkExternal>
                          </div>
                        }
                        onChange={(ev, checked) =>
                          setState((x) => ({ ...x, pp: checked }))
                        }
                      />
                    </FormGroup>
                  )}
                </FlexContainer>

                <StripeCheckoutButton
                  orgId={org.id}
                  disabled={!state.tos || !state.pp}
                  priceId={plan.priceId}
                  currency={plan.currency}
                />
              </Container>
            );
          })}
        </Section>
      )}
    </PageBody>
  );
};

export const PageOrgSubscription = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.org.branding)) {
    return <NoAccess />;
  }

  return (
    <StripeProvider>
      <ContentOrgSubscription />
    </StripeProvider>
  );
};
